// src/components/AddExecutiveFindingModal.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import {
  X, AlertTriangle, Shield, Lock, User, Eye,
  Globe, Calendar, FileText, CheckSquare, Plus
} from 'lucide-react';

const FINDING_TYPES = ['Compromised Credentials', 'Impersonating Account', 'Suspicious Mention'];
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
const STATUSES = ['Open', 'Investigating', 'Mitigated', 'Resolved', 'False Positive'];
const THREAT_LEVELS = ['Critical', 'High', 'Medium', 'Low'];

const defaultForm = {
  executive_id: '',
  finding_type: 'Compromised Credentials',
  title: '',
  severity: 'High',
  status: 'Open',
  source: '',
  platform: '',
  url: '',
  discovered_date: new Date().toISOString().split('T')[0],
  // Compromised Credentials
  compromised_email: '',
  password_exposed: false,
  data_breach_name: '',
  credential_count: '',
  // Impersonating Account
  impersonator_handle: '',
  impersonator_name: '',
  impersonator_profile_url: '',
  follower_count: '',
  verified: false,
  // Suspicious Mention
  mention_context: '',
  mention_url: '',
  threat_level: 'Medium',
  mention_type: '',
  // Common extended
  details: '',
  recommendations: '',
  notes: '',
  screenshot_url: '',
  // Flags
  executive_notified: false,
  law_enforcement_notified: false,
  reported_to_platform: false,
};

const AddExecutiveFindingModal = ({ isOpen, onClose, onFindingAdded }) => {
  const { getToken, isSignedIn } = useAuth();
  const { activeCompany } = useTenant();

  const [formData, setFormData] = useState({ ...defaultForm });
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExecs, setLoadingExecs] = useState(false);
  const [error, setError] = useState('');

  // Fetch executives for dropdown
  useEffect(() => {
    if (!isOpen || !isSignedIn || !activeCompany) return;
    const fetchExecs = async () => {
      setLoadingExecs(true);
      try {
        const token = await getToken();
        const res = await fetch(`/api/companies/${activeCompany.id}/executives`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setExecutives(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Failed to load executives:', e);
      } finally {
        setLoadingExecs(false);
      }
    };
    fetchExecs();
  }, [isOpen, isSignedIn, activeCompany, getToken]);

  const resetForm = () => {
    setFormData({ ...defaultForm });
    setError('');
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!formData.finding_type) {
      setError('Finding type is required.');
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const payload = {
        ...formData,
        executive_id: formData.executive_id ? parseInt(formData.executive_id) : null,
        credential_count: formData.credential_count ? parseInt(formData.credential_count) : null,
        follower_count: formData.follower_count ? parseInt(formData.follower_count) : null,
      };

      const res = await fetch(`/api/companies/${activeCompany.id}/executive-findings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create finding');

      console.log('✅ Executive finding created:', data.finding_id);
      if (onFindingAdded) onFindingAdded(data);
      resetForm();
      onClose();
    } catch (err) {
      console.error('❌ Error creating finding:', err);
      setError(err.message || 'Failed to create finding');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'Compromised Credentials') return Lock;
    if (type === 'Impersonating Account') return User;
    if (type === 'Suspicious Mention') return Eye;
    return AlertTriangle;
  };

  if (!isOpen) return null;

  const TypeIcon = getTypeIcon(formData.finding_type);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-red-500/30 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-red-900/30 to-slate-900/30 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Report Executive Finding
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Log a threat or security finding for an executive
            </p>
          </div>
          <button
            onClick={() => { resetForm(); onClose(); }}
            className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
            disabled={loading}
          >
            <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-300 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Section 1 — Core */}
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-400" />
              Core Information
            </h3>

            {/* Executive & Finding Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  Executive {loadingExecs && <span className="text-slate-500">(loading...)</span>}
                </label>
                <select
                  value={formData.executive_id}
                  onChange={e => set('executive_id', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  disabled={loading}
                >
                  <option value="">— Not linked to specific executive —</option>
                  {executives.map(exec => (
                    <option key={exec.executive_id} value={exec.executive_id}>
                      {exec.name}{exec.title ? ` — ${exec.title}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  Finding Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.finding_type}
                  onChange={e => set('finding_type', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  disabled={loading}
                >
                  {FINDING_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => set('title', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                placeholder="Brief description of the finding..."
                disabled={loading}
              />
            </div>

            {/* Severity, Status, Discovered Date */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Severity</label>
                <select
                  value={formData.severity}
                  onChange={e => set('severity', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  disabled={loading}
                >
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={e => set('status', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  disabled={loading}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  <Calendar className="w-3 h-3 inline-block mr-1" />
                  Discovered Date
                </label>
                <input
                  type="date"
                  value={formData.discovered_date}
                  onChange={e => set('discovered_date', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Source, Platform, URL */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Source</label>
                <input
                  type="text"
                  value={formData.source}
                  onChange={e => set('source', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="e.g., HaveIBeenPwned, Manual"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Platform</label>
                <input
                  type="text"
                  value={formData.platform}
                  onChange={e => set('platform', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="e.g., Twitter, LinkedIn, Dark Web"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  <Globe className="w-3 h-3 inline-block mr-1" />
                  URL / Link
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={e => set('url', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="https://..."
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Section 2 — Type-specific fields */}
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <TypeIcon className="w-4 h-4 text-orange-400" />
              {formData.finding_type} Details
            </h3>

            {/* COMPROMISED CREDENTIALS */}
            {formData.finding_type === 'Compromised Credentials' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Compromised Email</label>
                    <input
                      type="email"
                      value={formData.compromised_email}
                      onChange={e => set('compromised_email', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="executive@company.com"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Data Breach Name</label>
                    <input
                      type="text"
                      value={formData.data_breach_name}
                      onChange={e => set('data_breach_name', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="e.g., LinkedIn 2021, RockYou2024"
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Credential Count</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.credential_count}
                      onChange={e => set('credential_count', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="Number of credentials exposed"
                      disabled={loading}
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <input
                      type="checkbox"
                      id="password_exposed"
                      checked={formData.password_exposed}
                      onChange={e => set('password_exposed', e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500/30"
                      disabled={loading}
                    />
                    <label htmlFor="password_exposed" className="text-sm text-slate-300 cursor-pointer">
                      Password / hash exposed
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* IMPERSONATING ACCOUNT */}
            {formData.finding_type === 'Impersonating Account' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Impersonator Handle</label>
                    <input
                      type="text"
                      value={formData.impersonator_handle}
                      onChange={e => set('impersonator_handle', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="@fake_handle"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Impersonator Display Name</label>
                    <input
                      type="text"
                      value={formData.impersonator_name}
                      onChange={e => set('impersonator_name', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="Display name on the fake account"
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Impersonator Profile URL</label>
                  <input
                    type="url"
                    value={formData.impersonator_profile_url}
                    onChange={e => set('impersonator_profile_url', e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                    placeholder="https://twitter.com/fake_handle"
                    disabled={loading}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Follower Count</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.follower_count}
                      onChange={e => set('follower_count', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="Approx. follower count"
                      disabled={loading}
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <input
                      type="checkbox"
                      id="verified"
                      checked={formData.verified}
                      onChange={e => set('verified', e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500/30"
                      disabled={loading}
                    />
                    <label htmlFor="verified" className="text-sm text-slate-300 cursor-pointer">
                      Account has verified badge
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* SUSPICIOUS MENTION */}
            {formData.finding_type === 'Suspicious Mention' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Threat Level</label>
                    <select
                      value={formData.threat_level}
                      onChange={e => set('threat_level', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      disabled={loading}
                    >
                      {THREAT_LEVELS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Mention Type</label>
                    <input
                      type="text"
                      value={formData.mention_type}
                      onChange={e => set('mention_type', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="e.g., Doxxing, Threat, Harassment"
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Mention URL</label>
                  <input
                    type="url"
                    value={formData.mention_url}
                    onChange={e => set('mention_url', e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                    placeholder="https://link-to-mention.com"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Mention Context</label>
                  <textarea
                    rows={3}
                    value={formData.mention_context}
                    onChange={e => set('mention_context', e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                    placeholder="Describe the context of the mention and why it is suspicious..."
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3 — Analysis */}
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-cyan-400" />
              Analysis &amp; Evidence
            </h3>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Screenshot URL</label>
              <input
                type="url"
                value={formData.screenshot_url}
                onChange={e => set('screenshot_url', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                placeholder="https://link-to-screenshot.com"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Details</label>
              <textarea
                rows={3}
                value={formData.details}
                onChange={e => set('details', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                placeholder="Full technical details of the finding..."
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Recommendations</label>
              <textarea
                rows={3}
                value={formData.recommendations}
                onChange={e => set('recommendations', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                placeholder="Recommended actions to mitigate this finding..."
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Internal Notes</label>
              <textarea
                rows={2}
                value={formData.notes}
                onChange={e => set('notes', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                placeholder="Analyst notes (internal only)..."
                disabled={loading}
              />
            </div>
          </div>

          {/* Section 4 — Notification Flags */}
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-4">
              <CheckSquare className="w-4 h-4 text-purple-400" />
              Notifications &amp; Actions Taken
            </h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.executive_notified}
                  onChange={e => set('executive_notified', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500/30"
                  disabled={loading}
                />
                <span className="text-sm text-slate-300">Executive has been notified</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.law_enforcement_notified}
                  onChange={e => set('law_enforcement_notified', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500/30"
                  disabled={loading}
                />
                <span className="text-sm text-slate-300">Law enforcement notified</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.reported_to_platform}
                  onChange={e => set('reported_to_platform', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500/30"
                  disabled={loading}
                />
                <span className="text-sm text-slate-300">Reported to platform</span>
              </label>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={() => { resetForm(); onClose(); }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.title.trim()}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Report Finding
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddExecutiveFindingModal;